import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function Landing({ darkMode, onToggleTheme }) {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const handleSignIn = () => {
    navigate('/login');
  };

  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  // Professional Fortune 500 enterprise-grade color scheme
  const colors = {
    dark: {
      bg: '#0a0f1e',
      navBg: 'rgba(10, 15, 30, 0.95)',
      text: '#ffffff',
      subtext: '#94a3b8',
      accent: '#6366f1',
      accentHover: '#4f46e5',
      accentSolid: '#6366f1',
      cardBg: 'rgba(30, 41, 59, 0.5)',
      border: 'rgba(148, 163, 184, 0.1)',
      // Clean solid backgrounds
      heroBg: '#0a0f1e',
      featuresBg: '#0a0f1e',
      coachesBg: '#0a0f1e',
      pricingBg: '#0a0f1e',
    },
    light: {
      bg: '#ffffff',
      navBg: 'rgba(255, 255, 255, 0.85)',
      text: '#2d3748',
      subtext: '#64748b',
      accent: '#6366f1',
      accentHover: '#4f46e5',
      accentSolid: '#6366f1',
      cardBg: 'rgba(255, 255, 255, 0.8)',
      border: 'rgba(226, 232, 240, 0.5)',
      // Clean white backgrounds
      heroBg: '#ffffff',
      featuresBg: '#ffffff',
      coachesBg: '#ffffff',
      pricingBg: '#ffffff',
    }
  };

  const theme = darkMode ? colors.dark : colors.light;

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleSubscribe = (plan) => {
    // Redirect to login first - user must create account before subscribing
    navigate('/login');
  };

  const coaches = [
    { name: 'Nova', desc: 'Caring guide - Deeply empathetic, gentle, patient listener', avatar: '/jessica.png' },
    { name: 'Atlas', desc: 'Strategic leader - Confident, results-driven, professional excellence', avatar: '/george2.png' },
    { name: 'Sage', desc: 'Deep wisdom - Grounded, experienced, thoughtful mentor', avatar: '/roger.png' },
    { name: 'Phoenix', desc: 'Dynamic energy - Bold, transformative, innovative thinking', avatar: '/bill.png' },
    { name: 'Aurora', desc: 'Polished grace - Sophisticated, articulate, professional', avatar: '/rachel.png', imageStyle: { objectPosition: 'center 20%' } },
    { name: 'Catalyst', desc: 'Change agent - Inspiring, action-oriented, breakthrough focus', avatar: '/amelia.png' }
  ];

  return (
    <div style={{ 
      minHeight: '100vh',
      width: '100%',
      background: theme.bg,
      color: theme.text,
      fontFamily: 'Inter, system-ui, sans-serif',
      position: 'relative',
      overflowX: 'hidden',
      overflowY: 'auto',
      paddingTop: 0,
      marginTop: 0
    }}>

      {/* Content Container - scrollable */}
      <div style={{ position: 'relative', zIndex: 1, width: '100%' }}>
        {/* Navigation (fixed) */}
        <nav style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          background: theme.navBg,
          backdropFilter: 'blur(20px)',
          borderBottom: `1px solid ${theme.border}`,
          padding: '12px 5%',
          width: '100%'
        }}>
          <div style={{
            maxWidth: '1400px',
            margin: '0 auto',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: 48,
                height: 48,
                borderRadius: 12,
                background: 'white',
                border: darkMode
                  ? '1px solid rgba(255, 255, 255, 0.2)'
                  : '1px solid rgba(0, 0, 0, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: darkMode 
                  ? '0 2px 8px rgba(0, 0, 0, 0.3)' 
                  : '0 1px 3px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.2s ease'
              }}>
                <img 
                  src="/fidora_fav1.png" 
                  alt="Fidora"
                  style={{
                    width: 64,
                    height: 64,
                    objectFit: 'contain'
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
              <div style={{ 
                fontSize: '1.375rem', 
                fontWeight: 600, 
                color: darkMode ? '#f3f4f6' : '#1e293b',
                letterSpacing: '-0.025em'
              }}>
                Fidora
              </div>
            </div>
            
            {/* Desktop Navigation */}
            <div style={{ display: 'flex', gap: '32px', alignItems: 'center' }} className="desktop-nav">
              <button onClick={() => scrollToSection('features')} style={{ background: 'none', border: 'none', color: theme.text, cursor: 'pointer', fontSize: '0.95rem', fontWeight: 500 }}>Features</button>
              <button onClick={() => scrollToSection('coaches')} style={{ background: 'none', border: 'none', color: theme.text, cursor: 'pointer', fontSize: '0.95rem', fontWeight: 500 }}>Coaches</button>
              <button onClick={() => scrollToSection('pricing')} style={{ background: 'none', border: 'none', color: theme.text, cursor: 'pointer', fontSize: '0.95rem', fontWeight: 500 }}>Pricing</button>
              <button onClick={() => navigate('/login')} style={{ background: 'none', border: 'none', color: theme.text, cursor: 'pointer', fontSize: '0.95rem', fontWeight: 500 }}>Sign In</button>
              
              <button 
                onClick={onToggleTheme}
                style={{
                  background: 'transparent',
                  border: `1px solid ${theme.border}`,
                  borderRadius: '8px',
                  padding: '8px 12px',
                  color: theme.text,
                  cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
                  gap: '6px',
                  fontSize: '0.9rem'
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  {darkMode ? (
                    <>
                      <circle cx="12" cy="12" r="5"/>
                      <line x1="12" y1="1" x2="12" y2="3"/>
                      <line x1="12" y1="21" x2="12" y2="23"/>
                      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
                      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
                      <line x1="1" y1="12" x2="3" y2="12"/>
                      <line x1="21" y1="12" x2="23" y2="12"/>
                      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
                      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
                    </>
                  ) : (
                    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
                  )}
                </svg>
              </button>
              
              <button 
                onClick={handleSignIn} 
                style={{
                  background: theme.accent,
                  border: 'none',
                  borderRadius: '8px',
                  padding: '10px 20px',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  boxShadow: 'none',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = theme.accentHover;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = theme.accent;
                }}
              >
                Get Started
              </button>
            </div>
            
            {/* Mobile Hamburger Menu */}
            <div style={{ display: 'none' }} className="mobile-nav">
              <button 
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: theme.text,
                  cursor: 'pointer',
                  padding: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  {mobileMenuOpen ? (
                    <>
                      <line x1="18" y1="6" x2="6" y2="18"/>
                      <line x1="6" y1="6" x2="18" y2="18"/>
                    </>
                  ) : (
                    <>
                      <line x1="3" y1="12" x2="21" y2="12"/>
                      <line x1="3" y1="6" x2="21" y2="6"/>
                      <line x1="3" y1="18" x2="21" y2="18"/>
                    </>
                  )}
                </svg>
              </button>
            </div>
          </div>
          
          {/* Mobile Menu Dropdown */}
          {mobileMenuOpen && (
            <div style={{
              display: 'none',
              flexDirection: 'column',
              gap: '16px',
              padding: '20px 0',
              borderTop: `1px solid ${theme.border}`,
              marginTop: '12px'
            }} className="mobile-menu-dropdown">
              <button onClick={() => { scrollToSection('features'); setMobileMenuOpen(false); }} style={{ background: 'none', border: 'none', color: theme.text, cursor: 'pointer', fontSize: '1rem', fontWeight: 500, textAlign: 'left', padding: '8px 0' }}>Features</button>
              <button onClick={() => { scrollToSection('coaches'); setMobileMenuOpen(false); }} style={{ background: 'none', border: 'none', color: theme.text, cursor: 'pointer', fontSize: '1rem', fontWeight: 500, textAlign: 'left', padding: '8px 0' }}>Coaches</button>
              <button onClick={() => { scrollToSection('pricing'); setMobileMenuOpen(false); }} style={{ background: 'none', border: 'none', color: theme.text, cursor: 'pointer', fontSize: '1rem', fontWeight: 500, textAlign: 'left', padding: '8px 0' }}>Pricing</button>
              <button 
                onClick={onToggleTheme}
                style={{
                  background: 'transparent',
                  border: `1px solid ${theme.border}`,
                  borderRadius: '8px',
                  padding: '10px 16px',
                  color: theme.text,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '1rem',
                  fontWeight: 500
                }}
              >
                {darkMode ? 'Light Mode' : 'Dark Mode'}
              </button>
              <button onClick={() => { navigate('/login'); setMobileMenuOpen(false); }} style={{
                background: theme.accent,
                border: 'none',
                borderRadius: '8px',
                padding: '12px 24px',
                color: 'white',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 600,
                boxShadow: '0 4px 12px rgba(99, 102, 241, 0.4)'
              }}>
                Get Started
              </button>
            </div>
          )}
        </nav>
        {/* Spacer to offset fixed navbar height */}
        <div style={{ height: 72 }} />

        {/* Hero Section */}
        <section style={{
          minHeight: 'calc(100vh - 72px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '80px 5%',
          textAlign: 'center',
          background: theme.heroBg,
          position: 'relative',
          width: '100%'
        }}>
          <div style={{ maxWidth: '1100px', width: '100%' }}>
            <h1 style={{
              fontSize: 'clamp(2.5rem, 5vw, 4rem)',
              fontWeight: 600,
              lineHeight: 1.15,
              marginBottom: '28px',
              letterSpacing: '-0.02em'
            }}>
              Your AI Career Coach,<br/>
              <span style={{
                color: darkMode ? '#a78bfa' : '#8b5cf6',
                fontWeight: 400
              }}>Always Here for You</span>
          </h1>
            
            <p style={{
              fontSize: 'clamp(1.125rem, 2.2vw, 1.5rem)',
              color: darkMode ? '#c4b5fd' : '#7c3aed',
              marginBottom: '24px',
              lineHeight: 1.6,
              maxWidth: '850px',
              margin: '0 auto 24px',
              fontWeight: 600,
              fontStyle: 'italic'
            }}>
              "We believe people can live better lives when they find clarity in who they are and where they're going."
            </p>
            
            <p style={{
              fontSize: 'clamp(1rem, 2vw, 1.35rem)',
              color: theme.subtext,
              marginBottom: '48px',
              lineHeight: 1.7,
              maxWidth: '750px',
              margin: '0 auto 48px',
              fontWeight: 400
            }}>
              We're here to support your career journey every step of the way. Get personalized guidance, practice interviews, and build confidence with AI coaches who truly understand your goals.
            </p>
            
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap', flexDirection: 'column', alignItems: 'center' }}>
          <button
            onClick={() => navigate('/login')}
            style={{
                  background: theme.accent,
              border: 'none',
                  borderRadius: '8px',
                  padding: '14px 32px',
                  color: 'white',
                  fontSize: '1rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  boxShadow: darkMode ? '0 4px 12px rgba(99, 102, 241, 0.3)' : '0 2px 8px rgba(99, 102, 241, 0.25)',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = theme.accentHover;
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = darkMode ? '0 6px 16px rgba(99, 102, 241, 0.4)' : '0 4px 12px rgba(99, 102, 241, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = theme.accent;
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = darkMode ? '0 4px 12px rgba(99, 102, 241, 0.3)' : '0 2px 8px rgba(99, 102, 241, 0.25)';
                }}
              >
                Start Free Trial
              </button>
              <p style={{ 
                fontSize: '0.9rem', 
                color: theme.subtext,
                marginTop: '-8px',
                fontWeight: 500,
                opacity: 0.9
              }}>
                7 days free • No credit card required
              </p>
              
              <button 
                onClick={() => scrollToSection('coaches')}
                style={{
                  background: 'transparent',
                  border: `1px solid ${theme.border}`,
                  borderRadius: '8px',
                  padding: '14px 32px',
                  color: theme.text,
                  fontSize: '1rem',
              fontWeight: 600,
              cursor: 'pointer',
                  backdropFilter: 'blur(10px)',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = theme.accent;
                  e.currentTarget.style.color = theme.accent;
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = theme.border;
                  e.currentTarget.style.color = theme.text;
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                Meet Your Coaches
              </button>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section id="features" style={{ 
          padding: '100px 5%', 
          maxWidth: '1400px', 
          margin: '0 auto',
          background: theme.featuresBg,
          position: 'relative',
          width: '100%'
        }}>
          <div style={{ textAlign: 'center', marginBottom: '80px' }}>
            <h2 style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 700, marginBottom: '16px' }}>
              Everything You Need to Succeed
            </h2>
            <p style={{ fontSize: 'clamp(1rem, 1.8vw, 1.2rem)', color: theme.subtext, maxWidth: '600px', margin: '0 auto' }}>
              We've built powerful tools to help you land your dream job and grow in your career
            </p>
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '40px' }}>
            {[
              { title: 'Interview Preparation', desc: 'Practice makes perfect! Our AI coaches give you real-time feedback on your answers, helping you feel confident and prepared for any interview situation.' },
              { title: 'Resume & Job Search', desc: 'Let\'s make your profile shine! We\'ll help you craft a standout resume and actively search for job offerings that match your skills and career goals.' },
              { title: 'Career Strategy', desc: 'Together, we\'ll create a personalized roadmap for your success - whether you\'re aiming for a promotion, switching careers, or climbing the corporate ladder.' }
            ].map((item, i) => (
              <div key={i} style={{
                background: theme.cardBg,
                backdropFilter: 'blur(20px)',
                padding: '40px 32px',
                borderRadius: '24px',
                border: `1px solid ${theme.border}`,
                transition: 'all 0.3s',
                cursor: 'pointer',
                boxShadow: darkMode ? '0 4px 20px rgba(0,0,0,0.3)' : '0 4px 20px rgba(0,0,0,0.08)'
              }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-8px)';
                  e.currentTarget.style.boxShadow = darkMode ? '0 12px 40px rgba(139,92,246,0.3)' : '0 12px 40px rgba(139,92,246,0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = darkMode ? '0 4px 20px rgba(0,0,0,0.3)' : '0 4px 20px rgba(0,0,0,0.08)';
                }}
              >
                <h3 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '16px' }}>{item.title}</h3>
                <p style={{ color: theme.subtext, lineHeight: 1.7, fontSize: '1.05rem' }}>
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Pricing Section (moved above coaches) */}
        <section id="pricing" style={{ 
          padding: '100px 5%', 
          maxWidth: '1400px', 
          margin: '0 auto',
          background: theme.pricingBg,
          position: 'relative',
          width: '100%'
        }}>
          <div style={{ textAlign: 'center', marginBottom: '80px' }}>
            <h2 style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 700, marginBottom: '16px' }}>
              Start Your Journey Today
            </h2>
            <p style={{ fontSize: 'clamp(1rem, 1.8vw, 1.2rem)', color: theme.subtext, maxWidth: '600px', margin: '0 auto' }}>
              Choose the plan that fits your goals - you can upgrade or cancel anytime, no hard feelings!
            </p>
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '36px', maxWidth: '1100px', margin: '0 auto' }}>
            {[
              { 
                plan: 'Advisor', 
                price: '$9.99', 
                stripeLink: 'advisor',
                features: [
                  'Generous text chat allowance',
                  'Perfect for career planning',
                  'Get started with confidence',
                  'Ideal for students & early career'
                ]
              },
              { 
                plan: 'Coach', 
                price: '$14.99', 
                featured: true, 
                stripeLink: 'coach',
                features: [
                  '3 hours of voice chat per month',
                  'Ample text chat included',
                  '2 mock interview sessions',
                  'Tell your story with confidence',
                  'Most popular choice!'
                ]
              },
              { 
                plan: 'Mentor', 
                price: '$24.99', 
                stripeLink: 'mentor',
                features: [
                  '6 hours of voice chat per month',
                  'Extensive text chat allowance',
                  '4 mock interview sessions',
                  'Maximum flexibility',
                  'Add-ons available if needed'
                ]
              }
            ].map((p, i) => (
              <div key={i} style={{
                background: theme.cardBg,
                backdropFilter: 'blur(20px)',
                padding: '44px 36px',
                borderRadius: '24px',
                border: p.featured 
                  ? `3px solid ${theme.accentSolid}`
                  : `1px solid ${theme.border}`,
                textAlign: 'center',
                transition: 'all 0.3s',
                cursor: 'pointer',
                boxShadow: p.featured
                  ? (darkMode ? '0 8px 40px rgba(129,140,248,0.4)' : '0 8px 40px rgba(139,92,246,0.3)')
                  : (darkMode ? '0 4px 20px rgba(0,0,0,0.3)' : '0 4px 20px rgba(0,0,0,0.08)'),
                position: 'relative',
                transform: p.featured ? 'scale(1.05)' : 'scale(1)'
              }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-8px) scale(1.05)';
                  e.currentTarget.style.boxShadow = darkMode ? '0 12px 50px rgba(139,92,246,0.4)' : '0 12px 50px rgba(139,92,246,0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = p.featured ? 'translateY(0) scale(1.05)' : 'translateY(0) scale(1)';
                  e.currentTarget.style.boxShadow = p.featured
                    ? (darkMode ? '0 8px 40px rgba(129,140,248,0.4)' : '0 8px 40px rgba(139,92,246,0.3)')
                    : (darkMode ? '0 4px 20px rgba(0,0,0,0.3)' : '0 4px 20px rgba(0,0,0,0.08)');
                }}
              >
                {p.featured && (
                  <div style={{
                    position: 'absolute',
                    top: '-15px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: theme.accent,
                    color: 'white',
                    padding: '6px 20px',
                    borderRadius: '20px',
                    fontSize: '0.8rem',
                    fontWeight: 700,
                    boxShadow: '0 4px 12px rgba(139,92,246,0.4)'
                  }}>
                    MOST POPULAR
                  </div>
                )}
                <div style={{ fontWeight: 700, fontSize: '1.6rem', marginBottom: '12px' }}>{p.plan}</div>
                <div style={{ fontSize: '2.5rem', fontWeight: 800, margin: '16px 0', color: theme.accentSolid }}>
                  {p.price}
                  <span style={{ fontSize: '1rem', color: theme.subtext, fontWeight: 400 }}>/mo</span>
                </div>
                
                {/* Features List */}
                <div style={{ textAlign: 'left', marginBottom: '24px' }}>
                  {p.features.map((feature, idx) => (
                    <div key={idx} style={{ 
                      display: 'flex', 
                      alignItems: 'flex-start', 
                      marginBottom: '12px',
                      fontSize: '0.95rem',
                      color: theme.subtext,
                      lineHeight: 1.5
                    }}>
                      <span style={{ 
                        color: theme.accentSolid, 
                        marginRight: '8px', 
                        fontSize: '1rem',
                        fontWeight: 'bold'
                      }}>•</span>
                      <span>{feature}</span>
            </div>
          ))}
        </div>

                <button 
                  onClick={() => handleSubscribe(p.stripeLink)}
                  style={{
                    background: p.featured ? theme.accent : 'transparent',
                    color: p.featured ? 'white' : theme.accentSolid,
                    border: p.featured ? 'none' : `2px solid ${theme.accentSolid}`,
                    padding: '12px 32px',
                    borderRadius: '12px',
                    fontWeight: 700,
                    fontSize: '1rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    width: '100%'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.05)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                >
                  Get Started
                </button>
            </div>
          ))}
        </div>
        </section>

        {/* Coaches Section (now after pricing) */}
        <section id="coaches" style={{ 
          padding: '100px 5%', 
          maxWidth: '1400px', 
          margin: '0 auto',
          background: theme.coachesBg,
          position: 'relative',
          width: '100%'
        }}>
          <div style={{ textAlign: 'center', marginBottom: '80px' }}>
            <h2 style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 700, marginBottom: '16px' }}>
              Meet Your AI Coaches
            </h2>
            <p style={{ color: theme.subtext, fontSize: 'clamp(1rem, 1.8vw, 1.2rem)', maxWidth: '700px', margin: '0 auto' }}>
              All our coaches have the same expertise and knowledge - just pick the voice that feels right to you! Each one brings the same level of support and guidance to help you succeed.
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '36px' }}>
            {coaches.map((coach, i) => (
              <div key={i} style={{
                background: theme.cardBg,
                backdropFilter: 'blur(20px)',
                padding: '36px 28px',
                borderRadius: '24px',
                border: `1px solid ${theme.border}`,
                textAlign: 'center',
                transition: 'all 0.3s',
                cursor: 'pointer',
                boxShadow: darkMode ? '0 4px 20px rgba(0,0,0,0.3)' : '0 4px 20px rgba(0,0,0,0.08)'
              }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-8px)';
                  e.currentTarget.style.boxShadow = darkMode ? '0 12px 40px rgba(129,140,248,0.3)' : '0 12px 40px rgba(139,92,246,0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = darkMode ? '0 4px 20px rgba(0,0,0,0.3)' : '0 4px 20px rgba(0,0,0,0.08)';
                }}
              >
                <img 
                  src={coach.avatar} 
                  alt={`${coach.name} Avatar`}
                  style={{
                    width: 110,
                    height: 110,
                    borderRadius: '50%',
                    objectFit: 'cover',
                    marginBottom: 20,
                    border: `4px solid ${theme.accentSolid}`,
                    boxShadow: darkMode ? '0 8px 24px rgba(129,140,248,0.4)' : '0 8px 24px rgba(139,92,246,0.3)',
                    ...(coach.imageStyle || {})
                  }}
                />
                <div style={{ fontWeight: 700, fontSize: '1.4rem', marginBottom: 8 }}>{coach.name}</div>
                <p style={{ color: theme.subtext, fontSize: '1rem', lineHeight: 1.6 }}>{coach.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Mission Statement / Why We Exist */}
        <section style={{ 
          padding: '120px 5%', 
          maxWidth: '1100px', 
          margin: '0 auto',
          background: darkMode 
            ? 'linear-gradient(180deg, rgba(30, 41, 59, 0.3) 0%, rgba(15, 23, 42, 0.5) 100%)'
            : 'linear-gradient(180deg, rgba(250, 247, 242, 0.4) 0%, rgba(255, 255, 255, 0.6) 100%)',
          borderRadius: '32px',
          position: 'relative',
          width: '100%',
          border: `1px solid ${theme.border}`
        }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <h2 style={{ 
              fontSize: 'clamp(2rem, 4vw, 2.75rem)', 
              fontWeight: 700, 
              marginBottom: '16px',
              color: theme.text 
            }}>
              Why We Exist
            </h2>
            <div style={{ 
              width: '80px', 
              height: '4px', 
              background: theme.accent, 
              margin: '0 auto',
              borderRadius: '2px'
            }} />
          </div>
          
          <div style={{ 
            maxWidth: '900px', 
            margin: '0 auto',
            fontSize: 'clamp(1.05rem, 2vw, 1.25rem)',
            lineHeight: 1.9,
            color: theme.text,
            fontWeight: 400
          }}>
            <p style={{ marginBottom: '28px', textAlign: 'left' }}>
              Everyone wants a better life. Not a perfect one — just one that makes sense. Where what you do and who you are finally feel connected.
            </p>
            
            <p style={{ marginBottom: '28px', textAlign: 'left' }}>
              But somewhere along the way, growth got noisy. Advice got automated. Coaching turned into content. And clarity — the thing we all need most — got lost in the noise.
            </p>
            
            <p style={{ marginBottom: '28px', textAlign: 'left' }}>
              <strong style={{ fontWeight: 600 }}>Fidora was built to bring it back.</strong>
            </p>
            
            <p style={{ 
              marginBottom: '28px', 
              padding: '28px 32px',
              background: darkMode ? 'rgba(167, 139, 250, 0.1)' : 'rgba(139, 92, 246, 0.08)',
              borderLeft: `4px solid ${theme.accent}`,
              borderRadius: '8px',
              fontStyle: 'italic',
              fontSize: 'clamp(1.1rem, 2.2vw, 1.35rem)',
              fontWeight: 500,
              color: darkMode ? '#c4b5fd' : '#7c3aed'
            }}>
              Because we believe people can live better lives when they find clarity in who they are and where they're going.
            </p>
            
            <p style={{ marginBottom: '28px', textAlign: 'left' }}>
              That's the moment everything changes — when the fog lifts, when direction returns, when you can finally hear yourself again.
            </p>
            
            <p style={{ marginBottom: '28px', textAlign: 'left' }}>
              We don't give you answers. We give you space. We listen, we adapt, we help you see what's already there — your potential, your path, your truth.
            </p>
            
            <p style={{ marginBottom: '28px', textAlign: 'left' }}>
              This isn't about replacing human coaching. It's about making it accessible — personal, intelligent, and always present.
            </p>
            
            <p style={{ marginBottom: 0, textAlign: 'left', fontWeight: 500 }}>
              So wherever you are — on the edge of change, in the middle of confusion, or just ready for what's next — Fidora is here to help you move forward with clarity.
            </p>
            
            <p style={{ 
              marginTop: '36px', 
              textAlign: 'center',
              fontSize: 'clamp(1rem, 2vw, 1.2rem)',
              color: theme.accent,
              fontWeight: 600,
              fontStyle: 'italic'
            }}>
              Because clarity isn't found. It's built, one honest conversation at a time.
            </p>
          </div>
        </section>

        {/* Call to Action */}
        <section style={{ padding: '100px 5%', maxWidth: '1200px', margin: '0 auto', width: '100%' }}>
          <div style={{ 
            background: theme.accent, 
            padding: 'clamp(40px, 8vw, 70px) clamp(30px, 6vw, 50px)', 
            borderRadius: '32px', 
            color: 'white', 
            textAlign: 'center',
            boxShadow: darkMode ? '0 20px 60px rgba(139,92,246,0.4)' : '0 20px 60px rgba(139,92,246,0.35)',
            position: 'relative',
            overflow: 'hidden',
            width: '100%'
          }}>
            <div style={{
              position: 'absolute',
              top: '-50px',
              right: '-50px',
              width: '200px',
              height: '200px',
              background: 'rgba(255,255,255,0.1)',
              borderRadius: '50%',
              filter: 'blur(40px)'
            }} />
            <div style={{
              position: 'absolute',
              bottom: '-30px',
              left: '-30px',
              width: '150px',
              height: '150px',
              background: 'rgba(255,255,255,0.1)',
              borderRadius: '50%',
              filter: 'blur(40px)'
            }} />
            
            <div style={{ position: 'relative', zIndex: 1 }}>
              <h2 style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 800, marginBottom: '20px', letterSpacing: '-0.02em' }}>
                Ready to Take the Next Step?
              </h2>
              <p style={{ fontSize: 'clamp(1rem, 2vw, 1.3rem)', marginBottom: '40px', lineHeight: 1.7, opacity: 0.95 }}>
                Start your career transformation today. We're here to support you every step of the way!
              </p>
              <button 
                onClick={handleSignIn}
                style={{
                  background: 'white',
                  border: 'none',
                  borderRadius: '16px',
                  padding: '18px 48px',
                  color: '#8b5cf6',
                  fontSize: '1.15rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
                  transition: 'all 0.3s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-3px) scale(1.05)';
                  e.currentTarget.style.boxShadow = '0 15px 40px rgba(0,0,0,0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0) scale(1)';
                  e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.2)';
                }}
              >
                Start Your Free Trial
              </button>
            </div>
          </div>
        </section>

      {/* Footer */}
        <footer style={{ padding: '50px 5%', textAlign: 'center', color: theme.subtext, fontSize: '0.95rem', borderTop: `1px solid ${theme.border}`, marginTop: '60px', width: '100%' }}>
          <div style={{ marginBottom: '20px', fontSize: '1.1rem', fontWeight: 600 }}>
            We're here to help you succeed
          </div>
          <div style={{ marginBottom: '16px', display: 'flex', gap: '24px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a href="/terms.html" target="_blank" rel="noopener noreferrer" style={{ color: '#6366f1', textDecoration: 'none', fontWeight: 500, transition: 'opacity 0.2s' }} onMouseEnter={(e) => e.target.style.opacity = '0.7'} onMouseLeave={(e) => e.target.style.opacity = '1'}>
              Terms & Conditions
            </a>
            <span style={{ color: theme.border }}>•</span>
            <a href="/privacy.html" target="_blank" rel="noopener noreferrer" style={{ color: '#6366f1', textDecoration: 'none', fontWeight: 500, transition: 'opacity 0.2s' }} onMouseEnter={(e) => e.target.style.opacity = '0.7'} onMouseLeave={(e) => e.target.style.opacity = '1'}>
              Privacy Policy
            </a>
          </div>
          <div>© 2025 Fidora. All rights reserved.</div>
      </footer>
      </div>

      <style>{`
        /* Mobile Navigation */
        @media (max-width: 768px) {
          .desktop-nav {
            display: none !important;
          }
          .mobile-nav {
            display: flex !important;
          }
          .mobile-menu-dropdown {
            display: flex !important;
          }
          nav button {
            font-size: 0.85rem !important;
          }
        }
        
        @media (max-width: 640px) {
          nav {
            padding: 12px 3% !important;
          }
          section {
            padding: 60px 3% !important;
          }
        }
      `}</style>
    </div>
  );
}
